import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CoverImage from "./CoverImage";
import Badges from "../../components/Badges";
import SimpleLoader from "../../components/LogoLoader";

export default function CreatorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [creator, setCreator] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  // 👇 FETCH CREATOR
  const fetchCreator = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/curator/${id}`
      );
      const data = await res.json();
      setCreator(data && !data.message ? data : null);
    } catch (err) {
      console.error(err);
      setCreator(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 👇 SHARE CREATOR PROFILE
const handleShare = async () => {
  try {
    const shareUrl = window.location.href;

    const shareTitles = [
      `Found an incredible travel creator on Expeditio 🌍`,
      `Explore curated travel experiences by ${creator.fullName} ✈️`,
      `${creator.fullName}'s travel itineraries are unreal 👀`,
      `Luxury curated trips by ${creator.fullName} on Expeditio`,
      `Your next dream trip starts here 🌎`,
      `Discover hidden gems with ${creator.fullName}`,
    ];

    const randomTitle =
      shareTitles[Math.floor(Math.random() * shareTitles.length)];

    const shareText = `${randomTitle}

${creator.pitch || "Explore premium curated travel experiences."}

✨ Discover more on Expeditio`;

    // ✅ MOBILE NATIVE SHARE
    if (navigator.share) {
      await navigator.share({
        title: creator.fullName,
        text: shareText,
        url: shareUrl,
      });
    } else {
      // ✅ FALLBACK
      await navigator.clipboard.writeText(shareUrl);

      window.open(
        `https://wa.me/?text=${encodeURIComponent(
          `${shareText}\n\n${shareUrl}`
        )}`,
        "_blank"
      );
    }
  } catch (err) {
    console.log(err);
  }
};

  // 👇 FETCH TRIPS
  const fetchTrips = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/curated-trips/creator/${id}`
      );

      const data = await res.json();

      console.log("TRIPS DATA:", data);

      if (res.ok && Array.isArray(data)) {
        setTrips(data);
      } else {
        setTrips([]);
      }
    } catch (err) {
      console.error(err);
      setTrips([]);
    }
  }, [id]);

  // 👇 USE EFFECT
  useEffect(() => {
    fetchCreator();
    fetchTrips();
  }, [fetchCreator, fetchTrips]);

  if (loading) {
    return <SimpleLoader />;
  }

  if (!creator) {
    return (
      <div className="pt-24 text-center text-gray-500">
        Creator not found
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#f9fafb] flex flex-col md:flex-row font-sans">
      {/* 🔴 LEFT FULL PANEL */}
      <div className="w-full md:w-[30%] lg:w-[28%] 
bg-gradient-to-b from-[#4a5a00]/85 to-[#2d3800] text-white 
flex flex-col p-8 md:p-12 md:sticky md:top-0 md:h-screen 
shadow-2xl relative overflow-hidden 

rounded-b-[1.5rem] 
md:rounded-b-none md:rounded-r-[1.5rem]
">
        {/* Subtle accent texture */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

        <div className="flex flex-col h-full relative z-10 w-full max-w-sm mx-auto">
          {/* TOP */}
          <div className="flex items-center justify-between mb-10">
           
  <button
    onClick={() => navigate(-1)}
    className="
      inline-flex items-center gap-2
      px-4 py-2
      bg-white/10 hover:bg-white/20
      backdrop-blur-md
      rounded-full
      text-sm font-medium text-white
      transition-all
      border border-white/10
    "
  >
    ← Back
  </button>
       {/* SHARE */}
  <button
    onClick={handleShare}
    className="
      px-4 sm:px-5 py-2
      bg-white/10 backdrop-blur-md
      border border-white/10
      text-white
      text-[12px] sm:text-[13px]
      font-bold
      rounded-full
      shadow-sm
      hover:bg-white/20
      hover:border-white/20
      hover:shadow-md
      hover:scale-[1.02]
      active:scale-95
      transition-all duration-200
      flex items-center gap-1.5
      whitespace-nowrap
    "
  >
    <svg
      className="w-3.5 h-3.5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>

    Share Profile
  </button>

  </div>
             

            {/* PROFILE HEAD */}
            <div className="flex flex-col items-center md:items-start text-center md:text-left">
              {/* AVATAR */}
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-[2rem] overflow-hidden bg-white/10 border-4 border-white/20 shadow-xl mb-6">
                {creator.avatar ? (
                  <img
                    src={creator.avatar}
                    alt={creator.fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-4xl font-bold bg-[#3a4800]">
                    {creator.fullName?.[0]}
                  </div>
                )}
              </div>

              {/* NAME & META */}
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-2 drop-shadow-sm">
                {creator.fullName}
              </h1>

              {/* <div className="flex items-center gap-2 text-white/80 text-sm md:text-base font-medium mb-6 bg-black/20 px-4 py-1.5 rounded-full border border-white/10">
                <span>📍</span>
                {creator.location || "Global Nomad"}
              </div> */}

              {/* ✨ SHARE BUTTON */}
                  {/* <button
  onClick={handleShare}
  className="mt-3 px-4 sm:px-5 py-2 bg-white/10 backdrop-blur-md border border-white/10 text-white text-[12px] sm:text-[13px] font-bold rounded-full shadow-sm hover:bg-white/20 hover:border-white/20 hover:shadow-md hover:scale-[1.02] active:scale-95 transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap"
>
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
    />
  </svg>

  Share Profile
</button> */}
            </div>

            {/* BIO */}
            <div className="space-y-4 mb-8">
              <h3 className="text-xs font-bold text-white/50 uppercase tracking-widest">About</h3>
              <p className="text-base text-white/90 leading-relaxed font-normal bg-black/10 p-5 rounded-2xl border border-white/5">
                {creator.pitch || "Sharing curated travel experiences."}
              </p>
            </div>

            {/* TAGS */}
            <div className="mb-4">
              <Badges creator={creator} />
            </div>
          </div>

          {/* 🔻 BOTTOM LOGO */}
          {/* <div className="mt-auto pt-10 pb-4 hidden md:flex justify-center md:justify-start">
          <span className="italic text-2xl font-serif text-white/30 tracking-widest">
            EXPEDITIO
          </span>
        </div> */}
        </div>
    
      {/* ⚪ RIGHT SIDE (SCROLLABLE) */}
      <div className="w-full md:w-[70%] lg:w-[72%] md:h-screen md:overflow-y-auto px-6 py-12 md:px-12 md:py-16 bg-[#f9fafb]">
        <div className="max-w-4xl mx-auto">
          {/* 🔗 SOCIAL CARDS */}
          <h2 className="text-xl font-bold text-[#1f2d1f] mb-6 flex items-center gap-2">
            <span>Connect</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-5 mb-14">
            {creator.youtube?.link && (
              <a
                href={creator.youtube.link}
                target="_blank"
                rel="noreferrer"
                className="group bg-white rounded-3xl p-4 flex items-center gap-5 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
                  <img
                    src="data:image/webp;base64,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"
                    alt="youtube"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">YouTube</p>
                  <p className="text-sm font-semibold text-[#1f2d1f] truncate">
                    {creator.youtube.link.replace(/^https?:\/\/(www\.)?/, '')}
                  </p>
                </div>
                <span className="text-gray-300 group-hover:text-red-500 transition-colors">↗</span>
              </a>
            )}

            {creator.instagram?.link && (
              <a
                href={creator.instagram.link}
                target="_blank"
                rel="noreferrer"
                className="group bg-white rounded-3xl p-4 flex items-center gap-5 border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform">
                  <img
                    src="https://th.bing.com/th/id/OIP.NUFU5mhqhqOr82Ge-CwjawHaHv?w=155&h=180&c=7&r=0&o=7&dpr=1.3&pid=1.7&rm=3"
                    alt="instagram"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-0.5">Instagram</p>
                  <p className="text-sm font-semibold text-[#1f2d1f] truncate">
                    {creator.instagram.link.replace(/^https?:\/\/(www\.)?/, '')}
                  </p>
                </div>
                <span className="text-gray-300 group-hover:text-pink-500 transition-colors">↗</span>
              </a>
            )}
          </div>

          {/* 🔥 TRIPS */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#1f2d1f]">Curated Trips</h2>
            <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-semibold">{trips.length}</span>
          </div>

          {trips.length === 0 ? (
            <div className="bg-white p-16 rounded-3xl text-center shadow-sm border border-gray-100 flex flex-col items-center">
              <span className="text-4xl mb-4">✈️</span>
              <h3 className="text-lg font-bold text-[#1f2d1f] mb-2">No trips available</h3>
              <p className="text-gray-500">This creator hasn't published any trips yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              {(Array.isArray(trips) ? trips : []).map((trip) => (
                <div
                  key={trip._id}
                  onClick={() => navigate(`/curated/${trip._id}`)}
                  className="group cursor-pointer bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 flex flex-col"
                >
                  {/* IMAGE */}
                  <div className="h-56 relative overflow-hidden">
                    <div className="absolute inset-0 group-hover:scale-105 transition-transform duration-700">
                      <CoverImage city={trip.city} />
                    </div>

                    {/* GRADIENT */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent"></div>

                    {/* TITLE */}
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-xs font-semibold uppercase tracking-wider mb-1 opacity-90 flex items-center gap-1">
                        📍 {trip.city}
                      </p>
                      <h3 className="text-xl font-bold leading-tight line-clamp-2">
                        {trip.title}
                      </h3>
                    </div>
                  </div>

                  {/* 🔲 CONTENT */}
                  <div className="p-5 flex flex-col flex-1">
                    {/* DESCRIPTION */}
                    {trip.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4 leading-relaxed">
                        {trip.description}
                      </p>
                    )}

                    {/* CTA */}
                    <div className="mt-auto pt-2 flex items-center justify-end">
                      <span className="inline-flex items-center gap-1 text-[#5b6f00] text-sm font-bold bg-[#5b6f00]/5 px-4 py-2 rounded-xl group-hover:bg-[#5b6f00] group-hover:text-white transition-colors duration-300">
                        View <span className="group-hover:translate-x-1 transition-transform">→</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

    </div>
  );

}